// Payment routing console (admin-portal) — the PLATFORM view of PayGate's
// multi-provider routing (Phase 3): provider health, the capability registry,
// the routing rules the engine consults, and a side-effect-free route simulator.
// All reached through the gateway at /api/v1/paygate/admin/routing/* (JWT-authed,
// platform-role-guarded upstream). The global axios interceptor attaches the token.
import axios from 'axios';

const API_V1 = (import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1').replace(
  /\/api(\/v1)?\/?$/,
  '/api/v1',
);
const BASE = `${API_V1}/paygate/admin/routing`;

// ---- Provider health ----
export type Health = 'healthy' | 'degraded' | 'unavailable';
export interface ProviderStatus {
  provider: string;
  health: Health;
  channels?: string[];
  country?: string;
}

// ---- Capability registry ----
export interface Capability {
  provider_code: string;
  country: string;
  channel: string;
  currency: string;
  min_amount: number;
  max_amount?: number | null;
  enabled: boolean;
}

// ---- Routing rules ----
export interface Rule {
  id: string;
  tenant_id?: string | null;
  priority: number;
  version: number;
  status: string;
  country: string;
  channel: string;
  network?: string | null;
  currency?: string | null;
  min_amount: number;
  max_amount?: number | null;
  provider_code: string;
  fallback_provider_code?: string | null;
  health_aware: boolean;
  effective_from: string;
  effective_to?: string | null;
}

export interface RuleInput {
  tenant_id?: string | null;
  priority: number;
  country: string;
  channel: string;
  network?: string | null;
  currency?: string | null;
  min_amount: number;
  max_amount?: number | null;
  provider_code: string;
  fallback_provider_code?: string | null;
  health_aware: boolean;
}

// ---- Route preview ----
export interface PreviewInput {
  tenant_id?: string;
  country: string;
  channel: string;
  network?: string;
  currency?: string;
  amount: number;
}
export interface RouteDecision {
  provider: string;
  ruleId?: string | null;
  ruleVersion?: number;
  fallbackUsed: boolean;
  reason: string; // matched_rule | fallback | default_rail
}

export async function listProviders(country?: string): Promise<ProviderStatus[]> {
  const { data } = await axios.get(`${BASE}/providers`, { params: country ? { country } : {} });
  return data.providers ?? [];
}

export async function listCapabilities(country?: string): Promise<Capability[]> {
  const { data } = await axios.get(`${BASE}/capabilities`, { params: country ? { country } : {} });
  return data.capabilities ?? [];
}

export async function listRules(tenantId?: string): Promise<Rule[]> {
  const { data } = await axios.get(`${BASE}/rules`, { params: tenantId ? { tenantId } : {} });
  return data.rules ?? [];
}

export async function createRule(input: RuleInput): Promise<Rule> {
  const { data } = await axios.post(`${BASE}/rules`, input);
  return data;
}

export async function previewRoute(input: PreviewInput): Promise<RouteDecision> {
  const { data } = await axios.post(`${BASE}/preview`, input);
  return data;
}
